import { StandardEvents } from '@shopify/events';

/**
 * `<card-size-selector>` turns a click on a size button into an add-to-cart.
 *
 * The element wraps a minimal `<product-form-component>` (see product-card-sizes.liquid).
 * On click it sets the hidden variant input and submits the form; Horizon's
 * product-form-component handles the AJAX add and (if the theme's global auto-open
 * is enabled) the cart-drawer auto-opens.
 *
 * It is only rendered when the global `show_sizes_on_hover` setting is enabled,
 * so its mere presence means clicks should add to cart.
 *
 * When `data-open-cart="true"` (the "abrir carrito al agregar desde la talla"
 * setting), it opens the cart drawer once the add resolves — independent of the
 * theme's global auto-open setting, so only size adds trigger it.
 */
// Comfortable headroom above dev-store measured add latency (~1.2-1.4s). This watchdog only
// exists to recover a chip that somehow got stuck in `pending` through a path we didn't
// anticipate; it must never fire during a normal add.
const PENDING_WATCHDOG_MS = 5000;

// How long the `added` / `error` confirmation stays on the chip before it reverts to rest.
const RESOLVED_STATE_RESET_MS = 1200;

// Shown when an add fails without a server-provided reason -- the network-error catch in
// product-form.js, or the synchronous no-promise branch below where nothing was ever sent.
const GENERIC_ADD_ERROR_MESSAGE = 'No pudimos agregar esta talla al carrito. Intentá nuevamente.';

class CardSizeSelector extends HTMLElement {
  /**
   * `wasAriaDisabled` is the `aria-disabled` attribute value (or `null` if absent) the chip had
   * at rest, before this state cycle set it to `"true"` for `pending`. Carried across the whole
   * cycle (pending -> added/error -> reset) the same way the old `wasDisabled` boolean was, so
   * clearing the state restores the chip to whatever it looked like before -- see `#markState`.
   * @type {Map<HTMLButtonElement, { timer: number, wasAriaDisabled: string | null }>}
   */
  #resetTimers = new Map();

  /**
   * Each chip's current click "epoch": a counter bumped every time that chip is clicked.
   * `settle`'s async resolution (below) and the reset timer scheduled in `#markState` both
   * capture the epoch that was active when they started and compare it against this map's
   * live value before writing to the chip. A slow `/cart/add.js` can resolve after the
   * pending watchdog already reset the chip AND after the shopper re-clicked it -- without
   * this guard that late resolution would paint over the newer click's state and cancel its
   * in-flight watchdog. A WeakMap so a chip swapped out by a wholesale overlay re-render (see
   * the `!button.isConnected` check in `#markState`) doesn't pin its old button node in memory.
   * @type {WeakMap<HTMLButtonElement, number>}
   */
  #epochs = new WeakMap();

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);

    for (const entry of this.#resetTimers.values()) clearTimeout(entry.timer);
    this.#resetTimers.clear();
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const button =
      event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    // `button.disabled` still gates sold-out chips (a real `disabled` attribute rendered in the
    // markup, untouched by this feature). `pending` re-entry is guarded separately via
    // `data-state`: pending no longer sets the real `disabled` property (see `#markState`) so a
    // keyboard shopper's Enter press doesn't blur the chip and hide the overlay that's only
    // revealed by hover/`:focus-within` in the first place.
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.dataset.state === 'pending') return;

    // Never let the click navigate the underlying card link.
    event.preventDefault();
    event.stopPropagation();

    const form = this.querySelector('form');
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    const pfc = this.querySelector('product-form-component');
    if (!form || !variantInput || !pfc) return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    variantInput.value = variantId;

    // Bump this chip's epoch before anything else. `settle` below captures it by closure, and
    // its async branches compare against this map's live value before writing to the chip, so
    // a superseded click's late resolution can never win over a click that came after it.
    const epoch = (this.#epochs.get(button) ?? 0) + 1;
    this.#epochs.set(button, epoch);

    // Cada respuesta se ata al chip que la origino: con dos clicks rapidos en tallas
    // distintas hay dos listeners en vuelo, asi que sin esto la confirmacion caeria
    // siempre en el ultimo chip clickeado.
    this.#markState(button, 'pending');

    let consumed = false;

    // product-form.js dispatches this on `pfc` itself -- synchronously, within the same fetch
    // `.then()` turn that later resolves this click's cartLinesUpdate promise -- only on the
    // error path, carrying the actual reason (e.g. "Tu carrito ya tiene la cantidad máxima de
    // este artículo"). That promise's own resolution value never carries the message (see
    // CartLinesUpdatePayload's `detail`), so this is the only way to recover it for the live
    // region announcement below instead of always falling back to a generic message.
    let errorMessage = /** @type {string | null} */ (null);
    const captureError = (/** @type {any} */ errorEvent) => {
      errorMessage = typeof errorEvent?.error === 'string' && errorEvent.error ? errorEvent.error : null;
    };
    pfc.addEventListener(StandardEvents.cartError, captureError, { once: true });

    const settle = (/** @type {any} */ event) => {
      consumed = true;

      const promise = event?.promise;
      if (!promise) {
        // No promise means we have no evidence the add actually happened -- never paint
        // success without proof. This branch runs synchronously within this same click's
        // turn (cartLinesUpdate dispatches synchronously inside requestSubmit, see
        // product-form.js), so the epoch can't have moved on yet -- no guard needed here. It
        // also runs before the fetch that would ever trigger a `cartError` event, so there's
        // nothing waiting to capture -- remove the listener now instead of leaking it.
        pfc.removeEventListener(StandardEvents.cartError, captureError);
        this.#markState(button, 'error');
        this.#announce(pfc, errorMessage || GENERIC_ADD_ERROR_MESSAGE);
        return;
      }

      promise
        .then(/** @param {any} result */ (result) => {
          // `cartError` (if this add errored) dispatches synchronously inside the same
          // fetch `.then()` that eventually resolves this promise, so by now it has already
          // fired and `errorMessage` (if set) reflects it -- safe to stop listening.
          pfc.removeEventListener(StandardEvents.cartError, captureError);

          const didError = result?.detail?.didError;
          // This request's own /cart/add.js round trip can outlast the pending watchdog
          // (~5s) and the shopper can legitimately re-click in the meantime. If that
          // happened, this chip has moved on to a newer epoch -- only the newer click owns
          // the chip's visual state now, so skip painting over it. The cart genuinely
          // received the add either way, so the cart-open side effect still fires.
          if (this.#epochs.get(button) === epoch) {
            this.#markState(button, didError ? 'error' : 'added');
            this.#announce(
              pfc,
              didError ? errorMessage || GENERIC_ADD_ERROR_MESSAGE : this.#addedAnnouncement(button)
            );
          }
          if (this.dataset.openCart === 'true' && !didError) this.#openCart();
        })
        .catch(() => {
          pfc.removeEventListener(StandardEvents.cartError, captureError);
          if (this.#epochs.get(button) === epoch) {
            this.#markState(button, 'error');
            this.#announce(pfc, errorMessage || GENERIC_ADD_ERROR_MESSAGE);
          }
        });
    };

    // Listening on THIS product-form-component (rather than document) scopes the listener to
    // this chip's own add: product-form-component dispatches cartLinesUpdate on itself
    // (`this.dispatchEvent(...)` inside ProductFormComponent), so a listener bound there only
    // ever observes events whose target IS this exact element -- a foreign card's add can
    // never reach it, bubbling or not (verified empirically: eventPhase === AT_TARGET,
    // event.target === pfc).
    //
    // The `consumed` flag guards the other half of the original assumption: that the dispatch
    // is synchronous. It usually is (ProductFormComponent#handleSubmit -> #processAddToCart
    // dispatches before returning), but handleSubmit returns early -- without dispatching
    // anything -- while a variant change is in flight (see assets/product-form.js, the
    // `#variantChangeInProgress` branch around L302-312). That path isn't reachable from this
    // card today, but product-form.js is an upstream Horizon file this repo takes
    // bidirectional syncs into, so the precondition can change without anyone touching this
    // feature. If requestSubmit() returns without having dispatched, recover immediately
    // instead of leaving the chip pending (and disabled) forever.
    pfc.addEventListener(StandardEvents.cartLinesUpdate, settle, { once: true });

    form.requestSubmit();

    if (!consumed) {
      pfc.removeEventListener(StandardEvents.cartLinesUpdate, settle);
      pfc.removeEventListener(StandardEvents.cartError, captureError);
      this.#clearState(button);
    }
  };

  /**
   * Builds the Spanish success announcement, naming the size that was just added.
   * @param {HTMLButtonElement} button
   * @returns {string}
   */
  #addedAnnouncement(button) {
    const size = button.querySelector('.card-sizes__item-label')?.textContent?.trim();
    return size ? `Talla ${size} agregada al carrito.` : 'Talla agregada al carrito.';
  }

  /**
   * Writes a message into the card's existing live region so screen reader users hear the
   * outcome of a click. The node (`ref="liveRegion"`, `aria-live="assertive"`, `role="status"`)
   * is rendered by product-card-sizes.liquid and is one of ProductFormComponent's own
   * `requiredRefs`, so it's guaranteed to exist once `pfc` has connected. Nothing upstream ever
   * populates it for this card, though: assets/product-form.js's own announcement calls are
   * gated on refs (`allAddToCartContainers[0]?.refs.addToCartButton`) that this card's minimal
   * form never renders, so they silently no-op here. This fills the gap without touching that
   * file.
   * @param {Element} pfc
   * @param {string} message
   */
  #announce(pfc, message) {
    const liveRegion = /** @type {any} */ (pfc).refs?.liveRegion;
    if (!(liveRegion instanceof HTMLElement)) return;
    liveRegion.textContent = message;
  }

  /**
   * Applies a transient visual state to the clicked chip. `pending` is watched by a bounded
   * watchdog so an add that never resolves can't brick the chip; `added` / `error` clear
   * themselves after a short confirmation window.
   * @param {HTMLButtonElement} button
   * @param {'pending' | 'added' | 'error'} state
   */
  #markState(button, state) {
    const previous = this.#resetTimers.get(button);
    if (previous) clearTimeout(previous.timer);

    // Remember whatever `aria-disabled` value the chip had before this state cycle started
    // (e.g. sold-out chips render `aria-disabled="true"` statically in the markup, alongside a
    // real `disabled` attribute this feature never touches) so clearing the state restores
    // that instead of clobbering it. `pending` is communicated via `aria-disabled` rather than
    // the `disabled` property specifically so the click doesn't blur the chip: a keyboard
    // shopper's Enter press would otherwise move focus to <body> and, since the overlay is only
    // revealed by hover/`:focus-within`, hide the very confirmation the click was for. Re-entry
    // while pending is guarded separately in #handleClick via `data-state`.
    const wasAriaDisabled = previous ? previous.wasAriaDisabled : button.getAttribute('aria-disabled');

    button.dataset.state = state;
    if (state === 'pending') {
      button.setAttribute('aria-disabled', 'true');
    } else if (wasAriaDisabled === null) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', wasAriaDisabled);
    }

    const delay = state === 'pending' ? PENDING_WATCHDOG_MS : RESOLVED_STATE_RESET_MS;

    // Capture this cycle's epoch so the reset below can recognize itself as superseded. In
    // practice a newer #markState call for this button already clearTimeout'd this timer
    // before it could fire (every call above clears `previous.timer`) -- this check is
    // belt-and-suspenders against that invariant ever breaking in a future refactor.
    const stateEpoch = this.#epochs.get(button);

    const timer = setTimeout(() => {
      // The overlay is replaced wholesale on a colour swap, so the chip may be gone.
      this.#resetTimers.delete(button);
      if (this.#epochs.get(button) !== stateEpoch) return;
      if (!button.isConnected) return;
      button.removeAttribute('data-state');
      if (wasAriaDisabled === null) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', wasAriaDisabled);
      }
    }, delay);

    this.#resetTimers.set(button, { timer, wasAriaDisabled });
  }

  /**
   * Synchronously cancels any in-flight state/timer for a chip and restores it to rest, as if
   * `pending` had never been set. Used when a submit resolves without ever dispatching
   * cartLinesUpdate.
   * @param {HTMLButtonElement} button
   */
  #clearState(button) {
    const entry = this.#resetTimers.get(button);
    if (entry) clearTimeout(entry.timer);
    this.#resetTimers.delete(button);

    button.removeAttribute('data-state');
    if (!entry || entry.wasAriaDisabled === null) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', entry.wasAriaDisabled);
    }
  }

  /** Opens the cart drawer if it isn't already open. */
  #openCart() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function' && !(/** @type {any} */ (drawer).isOpen)) {
      /** @type {any} */ (drawer).open();
    }
  }
}

if (!customElements.get('card-size-selector')) {
  customElements.define('card-size-selector', CardSizeSelector);
}
